package fr.manyverse.app.infrastructure.repository

import fr.manyverse.app.domain.model.Universe
import fr.manyverse.app.domain.model.UniverseConfig
import fr.manyverse.app.domain.repository.UniverseRepository
import fr.manyverse.app.infrastructure.error.mapToDomainException
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.query.Columns
import kotlinx.coroutines.CancellationException
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

private const val UNIVERSES_TABLE = "universes"

class SupabaseUniverseRepository(
    private val supabaseClient: SupabaseClient
) : UniverseRepository {

    override suspend fun getCustomUniversesForUser(userId: String): Result<List<Universe>> {
        return try {
            val dtos = supabaseClient
                .from(UNIVERSES_TABLE)
                .select(Columns.ALL) {
                    filter { eq("created_by", userId) }
                }
                .decodeList<UniverseDto>()
            Result.success(dtos.map { it.toDomain() })
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "universe"))
        }
    }

    override suspend fun getUniverseById(id: String): Result<Universe?> {
        return try {
            val dtos = supabaseClient
                .from(UNIVERSES_TABLE)
                .select(Columns.ALL) {
                    filter { eq("id", id) }
                }
                .decodeList<UniverseDto>()
            Result.success(dtos.firstOrNull()?.toDomain())
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "universe"))
        }
    }

    override suspend fun createUniverse(
        name: String,
        config: UniverseConfig,
        createdBy: String
    ): Result<Universe> {
        return try {
            val insertDto = UniverseInsertDto(
                createdBy = createdBy,
                name = name,
                config = config
            )
            val created = supabaseClient
                .from(UNIVERSES_TABLE)
                .insert(insertDto) { select() }
                .decodeSingle<UniverseDto>()
            Result.success(created.toDomain())
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "universe"))
        }
    }

    override suspend fun updateUniverse(
        id: String,
        name: String,
        config: UniverseConfig
    ): Result<Universe> {
        return try {
            val updateDto = UniverseUpdateDto(
                name = name,
                config = config
            )
            val updated = supabaseClient
                .from(UNIVERSES_TABLE)
                .update(updateDto) {
                    filter { eq("id", id) }
                    select()
                }
                .decodeSingle<UniverseDto>()
            Result.success(updated.toDomain())
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "universe"))
        }
    }

    override suspend fun deleteUniverse(id: String): Result<Unit> {
        return try {
            supabaseClient
                .from(UNIVERSES_TABLE)
                .delete { filter { eq("id", id) } }
            Result.success(Unit)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "universe"))
        }
    }
}

@Serializable
private data class UniverseDto(
    @SerialName("id") val id: String,
    @SerialName("created_by") val createdBy: String,
    @SerialName("name") val name: String,
    @SerialName("config") val config: UniverseConfig
) {
    fun toDomain(): Universe = Universe(
        id = id,
        displayName = name,
        config = config
    )
}

@Serializable
private data class UniverseInsertDto(
    @SerialName("created_by") val createdBy: String,
    @SerialName("name") val name: String,
    @SerialName("config") val config: UniverseConfig
)

@Serializable
private data class UniverseUpdateDto(
    @SerialName("name") val name: String,
    @SerialName("config") val config: UniverseConfig
)
